import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Link, Routes, Route, useLocation } from 'react-router-dom';
import MarketingNav from '@/components/MarketingNav';
import AppNav from '@/components/AppNav';
import InstallPrompt from '@/components/InstallPrompt';
import ErrorBoundary from '@/components/ErrorBoundary';
import Seo from '@/components/Seo';
import { AuthProvider } from '@/context/AuthContext';
import './App.css';

const LandingPage = lazy(() => import('@/components/LandingPage'));
const ScholarGallery = lazy(() => import('@/components/ScholarGallery'));
const BinderView = lazy(() => import('@/components/BinderView'));
const FiqhMap = lazy(() => import('@/components/FiqhMap.tsx'));
const HadithGrading = lazy(() => import('@/components/HadithGrading'));
const PricingPage = lazy(() => import('@/components/PricingPage'));
const AboutPage = lazy(() => import('@/components/AboutPage'));
const SubmitScholar = lazy(() => import('@/components/SubmitScholar'));
const TeacherWorkspace = lazy(() => import('@/components/TeacherWorkspace'));
const PresentationMode = lazy(() => import('@/components/PresentationMode'));
const HadithRoutes = lazy(() => import('@/components/HadithRoutes'));
const LoginPage = lazy(() => import('@/components/LoginPage'));
const AccountPage = lazy(() => import('@/components/AccountPage'));
const Assignments = lazy(() => import('@/components/Assignments'));
const ClassDashboard = lazy(() => import('@/components/ClassDashboard'));
const StudentJoin = lazy(() => import('@/components/StudentJoin'));
const LegalPage = lazy(() => import('@/components/LegalPage'));
const ScholarProfilePage = lazy(() => import('@/components/ScholarProfilePage'));
const WorkProfilePage = lazy(() => import('@/components/WorkProfilePage'));
const EncyclopediaPage = lazy(() => import('@/components/EncyclopediaPage'));

const PageLoader = () => (
  <div className="flex flex-col items-center justify-center gap-3 min-h-[60vh]" role="status">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
    <p className="text-sm font-semibold text-emerald-800">Loading page…</p>
  </div>
);

// Single source of truth for routing: each entry drives both the <Route> list
// and which nav (marketing vs app) renders for that path, so the two can't drift.
const ROUTES: { path: string; element: React.ComponentType; marketing?: boolean }[] = [
  { path: '/', element: LandingPage, marketing: true },
  { path: '/gallery', element: ScholarGallery },
  { path: '/binder', element: BinderView },
  { path: '/fiqh-map', element: FiqhMap },
  { path: '/encyclopedia', element: EncyclopediaPage },
  { path: '/scholars/:id', element: ScholarProfilePage },
  { path: '/works/:id', element: WorkProfilePage },
  { path: '/hadith-grading', element: HadithGrading },
  { path: '/hadith-routes', element: HadithRoutes },
  { path: '/teacher', element: TeacherWorkspace },
  { path: '/teacher/present/:lessonId', element: PresentationMode },
  { path: '/pricing', element: PricingPage, marketing: true },
  { path: '/about', element: AboutPage, marketing: true },
  { path: '/submit', element: SubmitScholar },
  { path: '/login', element: LoginPage, marketing: true },
  { path: '/account', element: AccountPage },
  { path: '/teacher/classes', element: Assignments },
  { path: '/teacher/classes/:classId', element: ClassDashboard },
  { path: '/join/:joinCode', element: StudentJoin },
  { path: '/terms', element: LegalPage, marketing: true },
  { path: '/privacy', element: LegalPage, marketing: true },
  { path: '/refunds', element: LegalPage, marketing: true },
  { path: '/contact', element: LegalPage, marketing: true },
];

const MARKETING_PATHS = ROUTES.filter((route) => route.marketing).map((route) => route.path);

// Learning pages worth resuming; the landing hero sends returning users back here.
const LAST_VISIT_KEY = 'last-learning-path';
const RESUMABLE = ['/scholars/', '/works/', '/hadith-grading', '/hadith-routes', '/fiqh-map', '/encyclopedia', '/binder'];

function AppShell() {
  const location = useLocation();
  // Presentation mode and the student join flow are full-screen focus views with no app chrome.
  const isPresentation =
    location.pathname.startsWith('/teacher/present') || location.pathname.startsWith('/join/');
  const isMarketing = MARKETING_PATHS.includes(location.pathname);

  useEffect(() => {
    if (!RESUMABLE.some((prefix) => location.pathname.startsWith(prefix))) return;
    try { localStorage.setItem(LAST_VISIT_KEY, location.pathname + location.search); } catch { /* storage blocked */ }
  }, [location.pathname, location.search]);

  return (
    <div className="min-h-screen bg-white">
      {!location.pathname.startsWith('/scholars/') && !location.pathname.startsWith('/works/') && <Seo />}
      {!isPresentation && (isMarketing ? <MarketingNav /> : <AppNav />)}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {ROUTES.map(({ path, element: Element }) => (
            <Route key={path} path={path} element={<Element />} />
          ))}
        </Routes>
      </Suspense>
      {!isPresentation && <InstallPrompt />}
      {!isPresentation && <AppFooter />}
    </div>
  );
}

function AppFooter() {
  return (
    <footer className="border-t border-emerald-100 bg-white px-4 py-6 text-sm text-slate-500">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p>&copy; {new Date().getFullYear()} Ummah Cards</p>
        <nav className="flex flex-wrap gap-x-5 gap-y-2" aria-label="Legal and support">
          <Link to="/terms" className="hover:text-emerald-700">Terms</Link>
          <Link to="/privacy" className="hover:text-emerald-700">Privacy</Link>
          <Link to="/refunds" className="hover:text-emerald-700">Refunds</Link>
          <Link to="/contact" className="hover:text-emerald-700">Contact</Link>
        </nav>
      </div>
    </footer>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <AppShell />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
